<template>
  <CompanyAppLayout 
    page-title="AI Tax Optimization" 
    page-subtitle="AI-powered tax optimization and deduction suggestions"
  >
    <!-- Page Header -->
    <div class="mb-8 pt-8">
      <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
        <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
        AI Tax Optimization
      </div>
      <h1 class="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">AI Tax Optimization</h1>
      <p class="text-xl text-slate-300 mb-8 max-w-2xl">AI-powered tax optimization, deduction suggestions, and automated tax planning for companies</p>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400 mx-auto mb-4"></div>
        <p class="text-slate-400">Analyzing your tax data...</p>
      </div>
    </div>

    <!-- AI Optimization Overview -->
    <div v-else class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
      <div class="ai-tax-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Potential Savings</p>
          <p class="text-3xl font-bold text-white">${{ aiStats.potentialSavings.toLocaleString() }}</p>
          <p class="text-sm text-emerald-400">AI identified</p>
        </div>
      </div>

      <div class="ai-tax-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Deduction Opportunities</p>
          <p class="text-3xl font-bold text-white">{{ aiStats.deductionOpportunities }}</p>
          <p class="text-sm text-blue-400">found by AI</p>
        </div>
      </div>

      <div class="ai-tax-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Optimization Score</p>
          <p class="text-3xl font-bold text-white">{{ aiStats.optimizationScore }}%</p>
          <p class="text-sm text-amber-400">tax efficiency</p>
        </div>
      </div>

      <div class="ai-tax-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 00.078-2.183l1.562-1.562C15.802 8.249 16 9.1 16 10zm-5.165 3.913l1.58 1.58A5.98 5.98 0 0110 16a5.976 5.976 0 01-2.516-.552l1.562-1.562a4.006 4.006 0 001.789.027zm-4.677-2.796a4.002 4.002 0 01-.041-2.08l-.08.08-1.53-1.533A5.98 5.98 0 004 10c0 .954.223 1.856.619 2.657l1.54-1.54zm1.088-6.45A5.974 5.974 0 0110 4c.954 0 1.856.223 2.657.619l-1.54 1.54a4.002 4.002 0 00-2.08-.041l-.08-.08z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">AI Recommendations</p>
          <p class="text-3xl font-bold text-white">{{ aiStats.recommendations }}</p>
          <p class="text-sm text-purple-400">action items</p>
        </div>
      </div>
    </div>

    <!-- AI Analysis Results -->
    <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-8">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-white">AI Tax Analysis</h2>
        <button @click="runAIAnalysis" :disabled="isAnalyzing" class="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-600 text-white font-medium rounded-lg transition-colors">
          {{ isAnalyzing ? 'Analyzing...' : 'Run AI Analysis' }}
        </button>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Current Tax Situation -->
        <div>
          <h3 class="text-lg font-semibold text-white mb-4">Current Tax Situation</h3>
          <div class="space-y-4">
            <div class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
              <div>
                <p class="text-white font-medium">Estimated Tax Liability</p>
                <p class="text-sm text-slate-400">Based on current deductions</p>
              </div>
              <div class="text-right">
                <p class="text-white font-bold">${{ currentTaxSituation.estimatedLiability.toLocaleString() }}</p>
                <p class="text-sm text-red-400">High</p>
              </div>
            </div>
            <div class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
              <div>
                <p class="text-white font-medium">Current Deductions</p>
                <p class="text-sm text-slate-400">Total claimed</p>
              </div>
              <div class="text-right">
                <p class="text-white font-bold">${{ currentTaxSituation.currentDeductions.toLocaleString() }}</p>
                <p class="text-sm text-amber-400">Limited</p>
              </div>
            </div>
            <div class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
              <div>
                <p class="text-white font-medium">Tax Rate</p>
                <p class="text-sm text-slate-400">Effective rate</p>
              </div>
              <div class="text-right">
                <p class="text-white font-bold">{{ currentTaxSituation.taxRate }}%</p>
                <p class="text-sm text-red-400">Above average</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Optimized Scenario -->
        <div>
          <h3 class="text-lg font-semibold text-white mb-4">AI Optimized Scenario</h3>
          <div class="space-y-4">
            <div class="flex items-center justify-between p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <div>
                <p class="text-white font-medium">Optimized Tax Liability</p>
                <p class="text-sm text-slate-400">With AI suggestions</p>
              </div>
              <div class="text-right">
                <p class="text-white font-bold">${{ optimizedScenario.optimizedLiability.toLocaleString() }}</p>
                <p class="text-sm text-emerald-400">Reduced</p>
              </div>
            </div>
            <div class="flex items-center justify-between p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <div>
                <p class="text-white font-medium">Additional Deductions</p>
                <p class="text-sm text-slate-400">AI identified</p>
              </div>
              <div class="text-right">
                <p class="text-white font-bold">${{ optimizedScenario.additionalDeductions.toLocaleString() }}</p>
                <p class="text-sm text-emerald-400">New opportunities</p>
              </div>
            </div>
            <div class="flex items-center justify-between p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <div>
                <p class="text-white font-medium">Optimized Tax Rate</p>
                <p class="text-sm text-slate-400">Effective rate</p>
              </div>
              <div class="text-right">
                <p class="text-white font-bold">{{ optimizedScenario.optimizedTaxRate }}%</p>
                <p class="text-sm text-emerald-400">Improved</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- AI Recommendations -->
    <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-8">
      <h2 class="text-2xl font-bold text-white mb-6">AI Recommendations</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div v-for="recommendation in aiRecommendations" :key="recommendation.id" class="p-4 bg-slate-700/30 rounded-lg border border-slate-600/50">
          <div class="flex items-start justify-between mb-3">
            <div class="flex items-center space-x-3">
              <div class="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <svg class="w-4 h-4 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-white">{{ recommendation.title }}</h3>
            </div>
            <span class="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-full">
              ${{ recommendation.savings.toLocaleString() }}
            </span>
          </div>
          <p class="text-slate-300 mb-4">{{ recommendation.description }}</p>
          <div class="flex items-center justify-between">
            <span class="text-sm text-slate-400">{{ recommendation.category }}</span>
            <button @click="applyDeduction(recommendation)" class="px-3 py-1 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors">
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Deduction Opportunities -->
    <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-8">
      <h2 class="text-2xl font-bold text-white mb-6">Deduction Opportunities</h2>
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="border-b border-slate-700/50">
              <th class="text-left py-3 px-4 text-slate-400 font-medium">Deduction Type</th>
              <th class="text-left py-3 px-4 text-slate-400 font-medium">Description</th>
              <th class="text-left py-3 px-4 text-slate-400 font-medium">Potential Savings</th>
              <th class="text-left py-3 px-4 text-slate-400 font-medium">Difficulty</th>
              <th class="text-left py-3 px-4 text-slate-400 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="deduction in deductionOpportunities" :key="deduction.id" class="border-b border-slate-700/30">
              <td class="py-3 px-4 text-white font-medium">{{ deduction.type }}</td>
              <td class="py-3 px-4 text-slate-300">{{ deduction.description }}</td>
              <td class="py-3 px-4 text-emerald-400 font-bold">${{ deduction.savings.toLocaleString() }}</td>
              <td class="py-3 px-4">
                <span :class="[
                  'px-2 py-1 text-xs font-medium rounded-full',
                  deduction.difficulty === 'Easy' ? 'bg-emerald-500/20 text-emerald-400' :
                  deduction.difficulty === 'Medium' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-red-500/20 text-red-400'
                ]">
                  {{ deduction.difficulty }}
                </span>
              </td>
              <td class="py-3 px-4">
                <button @click="viewDetails(deduction)" class="text-emerald-400 hover:text-emerald-300 text-sm font-medium">
                  View Details
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Tax Planning Calendar -->
    <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-8">
      <h2 class="text-2xl font-bold text-white mb-6">Tax Planning Calendar</h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div v-for="event in planningEvents" :key="event.id" class="p-4 bg-slate-700/30 rounded-lg border border-slate-600/50">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-lg font-semibold text-white">{{ event.title }}</h3>
            <span :class="[
              'px-2 py-1 text-xs font-medium rounded-full',
              event.priority === 'High' ? 'bg-red-500/20 text-red-400' :
              event.priority === 'Medium' ? 'bg-amber-500/20 text-amber-400' :
              'bg-emerald-500/20 text-emerald-400'
            ]">
              {{ event.priority }}
            </span>
          </div>
          <p class="text-slate-300 mb-3">{{ event.description }}</p>
          <div class="flex items-center justify-between">
            <span class="text-sm text-slate-400">{{ formatDate(event.dueDate) }}</span>
            <button @click="scheduleEvent(event)" class="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors">
              Schedule
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- AI Tax Insights -->
    <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
      <h2 class="text-2xl font-bold text-white mb-6">AI Tax Insights</h2>
      <div class="space-y-4">
        <div v-for="insight in aiInsights" :key="insight.id" class="p-4 bg-slate-700/30 rounded-lg border border-slate-600/50">
          <div class="flex items-start space-x-3">
            <div class="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg class="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 00.078-2.183l1.562-1.562C15.802 8.249 16 9.1 16 10zm-5.165 3.913l1.58 1.58A5.98 5.98 0 0110 16a5.976 5.976 0 01-2.516-.552l1.562-1.562a4.006 4.006 0 001.789.027zm-4.677-2.796a4.002 4.002 0 01-.041-2.08l-.08.08-1.53-1.533A5.98 5.98 0 004 10c0 .954.223 1.856.619 2.657l1.54-1.54zm1.088-6.45A5.974 5.974 0 0110 4c.954 0 1.856.223 2.657.619l-1.54 1.54a4.002 4.002 0 00-2.08-.041l-.08-.08z" clip-rule="evenodd"/>
              </svg>
            </div>
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-white mb-2">{{ insight.title }}</h3>
              <p class="text-slate-300 mb-3">{{ insight.description }}</p>
              <div class="flex items-center space-x-4 text-sm">
                <span class="text-emerald-400 font-medium">Impact: ${{ insight.impact.toLocaleString() }}</span>
                <span class="text-slate-400">{{ insight.category }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </CompanyAppLayout>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useUserStore } from '@/stores/userStore'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'

const userStore = useUserStore()

// Reactive data
const isLoading = ref(true)
const isAnalyzing = ref(false)

const aiStats = ref({
  potentialSavings: 0,
  deductionOpportunities: 0,
  optimizationScore: 0,
  recommendations: 0
})

const currentTaxSituation = ref({
  estimatedLiability: 0,
  currentDeductions: 0,
  taxRate: 0
})

const optimizedScenario = ref({
  optimizedLiability: 0,
  additionalDeductions: 0,
  optimizedTaxRate: 0
})

const aiRecommendations = ref([])
const deductionOpportunities = ref([])
const planningEvents = ref([])
const aiInsights = ref([])

// Methods
const fetchAITaxData = async () => {
  try {
    const response = await fetch('/api/ai-tax/stats', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      aiStats.value = data.stats
      currentTaxSituation.value = data.currentSituation
      optimizedScenario.value = data.optimizedScenario
      aiRecommendations.value = data.recommendations
      deductionOpportunities.value = data.deductionOpportunities
      planningEvents.value = data.planningEvents
      aiInsights.value = data.insights
    }
  } catch (error) {
    console.error('Error fetching AI tax data:', error)
  } finally {
    isLoading.value = false
  }
}

const runAIAnalysis = async () => {
  try {
    isAnalyzing.value = true
    const response = await fetch('/api/ai-tax/analyze', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      await fetchAITaxData()
    }
  } catch (error) {
    console.error('Error running AI analysis:', error)
  } finally {
    isAnalyzing.value = false
  }
}

const applyDeduction = async (recommendation: any) => {
  try {
    const response = await fetch(`/api/ai-tax/recommendations/${recommendation.id}/apply`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      await fetchAITaxData()
    }
  } catch (error) {
    console.error('Error applying deduction:', error)
  }
}

const viewDetails = (deduction: any) => {
  console.log('Viewing details for:', deduction)
}

const scheduleEvent = async (event: any) => {
  try {
    const response = await fetch('/api/ai-tax/events/schedule', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ eventId: event.id })
    })
    
    if (response.ok) {
      console.log('Event scheduled successfully')
    }
  } catch (error) {
    console.error('Error scheduling event:', error)
  }
}

const formatDate = (date: string) => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(new Date(date))
}

// Lifecycle
onMounted(async () => {
  await fetchAITaxData()
})
</script>

<style scoped>
.ai-tax-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 hover:border-emerald-500/30 transition-all duration-300;
}
</style>
